import React, { forwardRef, useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Calendar } from 'lucide-react';
import styles from '../css/components/DatePicker.module.css';

const CustomInput = forwardRef(({ value, onClick, isToday, placeholder }, ref) => (
  <button className={styles.dateButton} onClick={onClick} ref={ref}>
    <Calendar className={styles.calendarIcon} size={16} />
    {value ? (isToday ? `Today ${value}` : value) : (placeholder || 'Select date')}
  </button>
));

const DatePickerComponent = ({
  selectedDate,
  onDateChange,
  allowTextInput = false,
  placeholder = 'Select date',
  showMonthYearDropdowns = false,
  dateFormat = 'dd/MM/yyyy',
  minDate,
  maxDate,
  disableFutureDates = false,
  isClearable,
  ...rest
}) => {
  const [date, setDate] = useState(selectedDate ?? null);
  const today = new Date();
  const isToday = date ? (date.toDateString() === today.toDateString()) : false;

  // If disableFutureDates is true and no maxDate is provided, set maxDate to today
  const effectiveMaxDate = disableFutureDates && !maxDate ? today : maxDate;

  useEffect(() => {
    setDate(selectedDate ?? null);
  }, [selectedDate]);

  const handleChange = (newDate) => {
    setDate(newDate);
    if (onDateChange) {
      onDateChange(newDate);
    }
  };

  const tryParseFromString = (val) => {
    if (!val) return null;
    const s = String(val).trim();
    if (!s) return null;
    const norm = s.replace(/[.\-\s]+/g, '/');
    // Prefer yyyy/MM/dd
    let m = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(norm);
    if (m) {
      const yyyy = Number(m[1]);
      const mm = Number(m[2]);
      const dd = Number(m[3]);
      const d = new Date(yyyy, mm - 1, dd);
      return isNaN(d.getTime()) ? null : d;
    }
    // Fallback dd/MM/yyyy or d/M/yyyy
    m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(norm);
    if (m) {
      const dd = Number(m[1]);
      const mm = Number(m[2]);
      const yyyy = Number(m[3]);
      const d = new Date(yyyy, mm - 1, dd);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const handleRawInput = (e) => {
    const val = e.target.value;
    if (val === '') {
      setDate(null);
      if (onDateChange) onDateChange(null);
      return;
    }
    const parsed = tryParseFromString(val);
    if (parsed && (!minDate || parsed >= minDate) && (!effectiveMaxDate || parsed <= effectiveMaxDate)) {
      setDate(parsed);
      if (onDateChange) onDateChange(parsed);
    }
  };

  const handleBlur = (e) => {
    const parsed = tryParseFromString(e.target.value);
    if (parsed && (!minDate || parsed >= minDate) && (!effectiveMaxDate || parsed <= effectiveMaxDate)) {
      setDate(parsed);
      if (onDateChange) onDateChange(parsed);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const parsed = tryParseFromString(e.target.value);
      if (parsed && (!minDate || parsed >= minDate) && (!effectiveMaxDate || parsed <= effectiveMaxDate)) {
        setDate(parsed);
        if (onDateChange) onDateChange(parsed);
      }
    }
  };

  return allowTextInput ? (
    <DatePicker
      selected={date}
      onChange={handleChange}
      dateFormat={dateFormat}
      placeholderText={placeholder}
      isClearable={typeof isClearable === 'boolean' ? isClearable : true}
      className={styles.input}
      onChangeRaw={handleRawInput}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      showMonthDropdown={showMonthYearDropdowns}
      showYearDropdown={showMonthYearDropdowns}
      scrollableYearDropdown={showMonthYearDropdowns}
      yearDropdownItemNumber={showMonthYearDropdowns ? 100 : undefined}
      dropdownMode={showMonthYearDropdowns ? 'select' : undefined}
      popperPlacement="bottom-end"
      wrapperClassName={styles.wrapper}
      minDate={minDate}
      maxDate={effectiveMaxDate}
      {...rest}
    />
  ) : (
    <DatePicker
      selected={date}
      onChange={handleChange}
      dateFormat={dateFormat}
      customInput={<CustomInput isToday={isToday} placeholder={placeholder} />}
      showMonthDropdown={showMonthYearDropdowns}
      showYearDropdown={showMonthYearDropdowns}
      scrollableYearDropdown={showMonthYearDropdowns}
      yearDropdownItemNumber={showMonthYearDropdowns ? 100 : undefined}
      dropdownMode={showMonthYearDropdowns ? 'select' : undefined}
      popperPlacement="bottom-end"
      wrapperClassName={styles.wrapper}
      minDate={minDate}
      maxDate={effectiveMaxDate}
      {...rest}
    />
  );
};

export default DatePickerComponent;